//./react-app/src/scenes/sold-leads/index.tsx
import Header from '../../components/Header';
import React, { FC, useContext, useState } from 'react';
import { Box, ToggleButton, ToggleButtonGroup, Typography, useTheme } from '@mui/material';
import { tokens } from '../../theme';
import TableWidget, { HeaderMappingItem } from '../../helpers/tableBuilder';
import fetchData from '../../helpers/fetchData';
import { dateToSqlDate } from '../../helpers/dateFuncs';
import { FilterContext, IFilterState } from '../../contexts/FilterContext';
import ReportDetailModal from './ReportDetailModal';

// --- Types ---

interface VerlagRow {
  verlag: string;
  umsatz: number;
  lead_price: number;
  leads: number;
}

interface VerlagPriceRow {
  verlag: string;
  lead_price: number;
  umsatz: number;
  leads: number;
}

interface ReportRow {
  verlag: string;
  thema: string;
  umsatz: number;
  lead_price: number;
  leads: number;
}

interface SegmentRow {
  segment: string;
  umsatz: number;
  lead_price: number;
  leads: number;
}

// --- Intern / Extern ---
//
// Same split the monthly "Verkaufte Leads" mail sends out: the backend derives it
// from `e_affiliate_id` (own-send tracking tokens = intern, token-free = the
// external partner list), so page and mail can't disagree.
//
// Only meaningful for DNV Media — the token convention lives on that network's
// KOOP tags. On every other network the two sections would be an artefact, so the
// whole block only appears once DNV Media is among the selected networks.
type SegmentFilter = 'all' | 'intern' | 'extern';

const DNV_NETWORK = 'DNV Media';

const SEGMENT_LABELS: Record<string, string> = {
  intern: 'Interne Listen',
  extern: 'Externe Liste Matthias Deuschel',
};

// --- Header Mappings ---

const verlagHeaders: Record<string, HeaderMappingItem> = {
  verlag: { name: 'Verlag', type: 'string' },
  umsatz: { name: 'Umsatz', type: 'number', digits: 2, attach: ' €' },
  lead_price: { name: 'Ø Lead Preis', type: 'number', digits: 2, attach: ' €' },
  leads: { name: 'Leads', type: 'number', digits: 0 },
};

const verlagPriceHeaders: Record<string, HeaderMappingItem> = {
  verlag: { name: 'Verlag', type: 'string' },
  lead_price: { name: 'Lead Preis', type: 'number', digits: 2, attach: ' €' },
  umsatz: { name: 'Umsatz', type: 'number', digits: 2, attach: ' €' },
  leads: { name: 'Leads', type: 'number', digits: 0 },
};

const reportHeaders: Record<string, HeaderMappingItem> = {
  verlag: { name: 'Verlag', type: 'string' },
  thema: { name: 'Report', type: 'string' },
  umsatz: { name: 'Umsatz', type: 'number', digits: 2, attach: ' €' },
  lead_price: { name: 'Lead Preis', type: 'number', digits: 2, attach: ' €' },
  leads: { name: 'Leads', type: 'number', digits: 0 },
};

const segmentHeaders: Record<string, HeaderMappingItem> = {
  segment: { name: 'Herkunft', type: 'string' },
  umsatz: { name: 'Umsatz', type: 'number', digits: 2, attach: ' €' },
  lead_price: { name: 'Ø Lead Preis', type: 'number', digits: 2, attach: ' €' },
  leads: { name: 'Leads', type: 'number', digits: 0 },
};

// --- getData Functions ---

/** Shared query params — `segment` is omitted for 'all' so the URL stays as it was. */
const baseParams = (filterState: IFilterState, grouping: string, segment: SegmentFilter) => ({
  start_date: dateToSqlDate(filterState.startDate!),
  end_date: dateToSqlDate(filterState.endDate!),
  grouping,
  network: Array.isArray(filterState.network) ? filterState.network : [filterState.network],
  verlag: Array.isArray(filterState.verlag) ? filterState.verlag : [],
  ...(segment === 'all' ? {} : { segment }),
});

const getVerlagData = async (
  filterState: IFilterState,
  segment: SegmentFilter,
): Promise<VerlagRow[]> => {
  if (!filterState.startDate || !filterState.endDate) return [];
  return (await fetchData(
    '/api/vnr/sold-leads-summary',
    baseParams(filterState, 'verlag', segment),
  )) as VerlagRow[];
};

const getVerlagPriceData = async (
  filterState: IFilterState,
  segment: SegmentFilter,
): Promise<VerlagPriceRow[]> => {
  if (!filterState.startDate || !filterState.endDate) return [];
  return (await fetchData(
    '/api/vnr/sold-leads-summary',
    baseParams(filterState, 'verlag_price', segment),
  )) as VerlagPriceRow[];
};

const getReportData = async (
  filterState: IFilterState,
  segment: SegmentFilter,
): Promise<ReportRow[]> => {
  if (!filterState.startDate || !filterState.endDate) return [];
  return (await fetchData(
    '/api/vnr/sold-leads-summary',
    baseParams(filterState, 'report', segment),
  )) as ReportRow[];
};

// Deliberately never segment-filtered: this table IS the split, so it has to show
// both rows regardless of which segment the tables below are narrowed to.
const getSegmentData = async (filterState: IFilterState): Promise<SegmentRow[]> => {
  if (!filterState.startDate || !filterState.endDate) return [];
  const rows = (await fetchData(
    '/api/vnr/sold-leads-summary',
    baseParams(filterState, 'segment', 'all'),
  )) as SegmentRow[];
  return rows.map((row) => ({ ...row, segment: SEGMENT_LABELS[row.segment] ?? row.segment }));
};

// --- computeSummary Functions ---

const computeVerlagSummary = (data: VerlagRow[]): VerlagRow => {
  const totalUmsatz = data.reduce((sum, row) => sum + (Number(row.umsatz) || 0), 0);
  const totalLeads = data.reduce((sum, row) => sum + (Number(row.leads) || 0), 0);
  return {
    verlag: 'Insgesamt',
    umsatz: totalUmsatz,
    lead_price: totalLeads > 0 ? totalUmsatz / totalLeads : 0,
    leads: totalLeads,
  };
};

const computeVerlagPriceSummary = (data: VerlagPriceRow[]): VerlagPriceRow => {
  const totalUmsatz = data.reduce((sum, row) => sum + (Number(row.umsatz) || 0), 0);
  const totalLeads = data.reduce((sum, row) => sum + (Number(row.leads) || 0), 0);
  return {
    verlag: 'Insgesamt',
    lead_price: totalLeads > 0 ? totalUmsatz / totalLeads : 0,
    umsatz: totalUmsatz,
    leads: totalLeads,
  };
};

const computeReportSummary = (data: ReportRow[]): ReportRow => {
  const totalUmsatz = data.reduce((sum, row) => sum + (Number(row.umsatz) || 0), 0);
  const totalLeads = data.reduce((sum, row) => sum + (Number(row.leads) || 0), 0);
  return {
    verlag: 'Insgesamt',
    thema: '',
    umsatz: totalUmsatz,
    lead_price: totalLeads > 0 ? totalUmsatz / totalLeads : 0,
    leads: totalLeads,
  };
};

const computeSegmentSummary = (data: SegmentRow[]): SegmentRow => {
  const totalUmsatz = data.reduce((sum, row) => sum + (Number(row.umsatz) || 0), 0);
  const totalLeads = data.reduce((sum, row) => sum + (Number(row.leads) || 0), 0);
  return {
    segment: 'Insgesamt',
    umsatz: totalUmsatz,
    lead_price: totalLeads > 0 ? totalUmsatz / totalLeads : 0,
    leads: totalLeads,
  };
};

// --- Component ---

const SoldLeads: FC = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { filterState } = useContext(FilterContext)!;
  const [selectedReport, setSelectedReport] = useState<string | null>(null);
  const [segment, setSegment] = useState<SegmentFilter>('all');

  const networks = Array.isArray(filterState.network)
    ? filterState.network
    : filterState.network
      ? [filterState.network]
      : [];
  const showSegments = networks.includes(DNV_NETWORK);

  // The toggle survives in state when DNV is deselected, so read it through this
  // instead of `segment` — otherwise a hidden control would keep filtering a
  // non-DNV view with no way to switch it back off.
  const activeSegment: SegmentFilter = showSegments ? segment : 'all';

  // TableWidget only refetches on `filterState`; `segment` lives outside it, so it
  // has to be part of the remount key like the filters already are.
  const filterKey = `${dateToSqlDate(filterState.startDate!)}-${dateToSqlDate(filterState.endDate!)}-${JSON.stringify(filterState.network)}-${JSON.stringify(filterState.verlag)}`;

  const segmentSuffix =
    activeSegment === 'all' ? '' : ` — ${SEGMENT_LABELS[activeSegment]}`;

  return (
    <Box m="20px">
      <Box justifyContent="space-between" alignItems="center" display="flex">
        <Header
          title="Verkaufte Leads"
          subtitle="VNR Lead-Kosten Analyse nach Verlag, Report und Kanal"
        />
      </Box>
      <Box>
        <Typography variant="h5" color={colors.blueAccent[400]}>
          <p>Ein Klick auf einen Report öffnet die Detailansicht nach Datum.</p>
        </Typography>
      </Box>

      {showSegments && (
        <Box mb="20px">
          <Typography variant="h5" fontWeight="600" mb={1}>
            Aufteilung Interne Listen / Externe Liste
          </Typography>
          <Typography variant="body2" color={colors.grey[300]} mb={1}>
            Gleiche Aufteilung wie im Monatsbericht „Verkaufte Leads“: Affiliate-Kennungen mit
            eigener Versand-Kennzeichnung zählen als interne Listen, alle übrigen als externe.
          </Typography>
          <TableWidget<SegmentRow>
            key={`segment-overview-${filterKey}`}
            getData={getSegmentData}
            computeSummary={computeSegmentSummary}
            detailsHeaderMapping={segmentHeaders}
            initialSortColumn="umsatz"
            sortOrder="desc"
            excelDownloadName="sold_leads_segment"
          />
          <Box mt={2}>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={segment}
              onChange={(_e, value) => value && setSegment(value as SegmentFilter)}
            >
              <ToggleButton value="all">Gesamt</ToggleButton>
              <ToggleButton value="intern">Nur Intern</ToggleButton>
              <ToggleButton value="extern">Nur Extern</ToggleButton>
            </ToggleButtonGroup>
            <Typography variant="body2" color={colors.grey[300]} mt={1}>
              Filtert die drei Tabellen darunter.
            </Typography>
          </Box>
        </Box>
      )}

      <Box display="grid" gridTemplateColumns="1fr" gap="20px">
        {/* Table 1: Grouped by Verlag */}
        <Box>
          <Typography variant="h5" fontWeight="600" mb={1}>
            Umsatz & Leads gruppiert nach Verlag{segmentSuffix}
          </Typography>
          <TableWidget<VerlagRow>
            key={`verlag-${filterKey}-${activeSegment}`}
            getData={(state) => getVerlagData(state, activeSegment)}
            computeSummary={computeVerlagSummary}
            detailsHeaderMapping={verlagHeaders}
            initialSortColumn="umsatz"
            sortOrder="desc"
            excelDownloadName="sold_leads_verlag"
          />
        </Box>

        {/* Table 2: Grouped by Verlag & Lead Preis */}
        <Box>
          <Typography variant="h5" fontWeight="600" mb={1}>
            Umsatz & Leads gruppiert nach Verlag & Lead Preis{segmentSuffix}
          </Typography>
          <TableWidget<VerlagPriceRow>
            key={`verlag-price-${filterKey}-${activeSegment}`}
            getData={(state) => getVerlagPriceData(state, activeSegment)}
            computeSummary={computeVerlagPriceSummary}
            detailsHeaderMapping={verlagPriceHeaders}
            initialSortColumn="lead_price"
            sortOrder="asc"
            excelDownloadName="sold_leads_verlag_lead_preis"
          />
        </Box>

        {/* Table 3: Grouped by Report (click to open detail modal) */}
        <Box>
          <Typography variant="h5" fontWeight="600" mb={1}>
            Umsatz & Leads gruppiert nach Report{segmentSuffix}
          </Typography>
          <TableWidget<ReportRow>
            key={`report-${filterKey}-${activeSegment}`}
            getData={(state) => getReportData(state, activeSegment)}
            computeSummary={computeReportSummary}
            detailsHeaderMapping={reportHeaders}
            initialSortColumn="umsatz"
            sortOrder="desc"
            excelDownloadName="sold_leads_report"
            onRowClick={(row: unknown) => setSelectedReport((row as ReportRow).thema)}
          />
        </Box>

        {/* TODO: Channel table disabled — adserver_lead_cache returns empty sources for most channels. Fix cache, then re-enable <SoldLeadsTable />. */}
      </Box>

      <ReportDetailModal
        open={selectedReport !== null}
        onClose={() => setSelectedReport(null)}
        thema={selectedReport}
        startDate={filterState.startDate}
        endDate={filterState.endDate}
        network={filterState.network}
        verlag={filterState.verlag}
        segment={activeSegment}
      />
    </Box>
  );
};

export default SoldLeads;
